import { ROLES, type GameView } from "@coup/shared";
import { ROLE_ART } from "../../art";
import { CardBack, CardFace } from "../../components/Card";
import { Icon } from "../../components/Icon";
import type { Names } from "./names";
import styles from "./Table.module.css";

const PILE = [
  [0, 30],
  [22, 32],
  [44, 30],
  [66, 32],
  [10, 16],
  [34, 18],
  [56, 16],
  [24, 3],
];

interface Props {
  view: GameView;
  names: Names;
  menu?: string;
  onOpen?: (menu: "treasury" | "deck") => void;
}

export function Treasury({ view, menu, onOpen }: Props) {
  const coins = Math.min(PILE.length, Math.ceil(view.treasury / 5));
  return (
    <button
      type="button"
      className={styles.pileButton}
      style={{
        left: 305,
        top: 350,
        outline: menu === "treasury" ? "2px solid var(--gold)" : "none",
        cursor: onOpen ? "pointer" : "default",
      }}
      aria-label={`Treasury: ${view.treasury} coins`}
      onClick={() => onOpen?.("treasury")}
      data-treasury
    >
      <div style={{ position: "relative", width: 96, height: 58 }}>
        {PILE.slice(0, coins).map(([l, t], i) => (
          <div key={i} className={styles.pileCoin} style={{ left: l, top: t }} />
        ))}
      </div>
      <div className={styles.pileLabel}>Treasury · {view.treasury}</div>
    </button>
  );
}

export function Deck({ view, menu, onOpen }: Props) {
  return (
    <button
      type="button"
      className={styles.pileButton}
      style={{
        left: 555,
        top: 350,
        outline: menu === "deck" ? "2px solid var(--gold)" : "none",
        cursor: onOpen ? "pointer" : "default",
      }}
      aria-label={`Court deck: ${view.deckCount} cards`}
      onClick={() => onOpen?.("deck")}
      data-deck
    >
      <div style={{ position: "relative", width: 64, height: 82 }}>
        <div className={styles.deckUnder} style={{ left: 6, top: 0, background: "#4a1c1f" }} />
        <div className={styles.deckUnder} style={{ left: 3, top: 3, background: "#4f1e21" }} />
        <CardBack size="deck" className={styles.deckTop} />
      </div>
      <div className={styles.pileLabel}>Court deck · {view.deckCount}</div>
    </button>
  );
}

/** Lost cards face up, grouped by role with a count and who lost them. */
export function Exiled({ view, names }: Props) {
  return (
    <div className={styles.exiled} data-exiled>
      <div className={styles.exiledLabel}>Exiled</div>
      <div style={{ display: "flex", gap: 10 }}>
        {ROLES.map((role) => {
          const lost = view.exiled.filter((e) => e.role === role);
          const who = lost.length
            ? `Lost by ${lost.map((e) => names.name(e.playerId)).join(", ")}`
            : "None lost";
          return (
            <div
              key={role}
              title={who}
              style={{ position: "relative", width: 44, height: 62 }}
              data-exiled-role={role}
            >
              {lost.length ? (
                <>
                  <CardFace role={role} size="mini" />
                  <div className={styles.exiledCount}>×{lost.length}</div>
                </>
              ) : (
                <div className={styles.exiledEmpty}>
                  <Icon path={ROLE_ART[role].icon} size={18} color="#7d705d" width={1.8} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
